import {FC, useCallback, useEffect, useState} from 'react';
import {useCabinetTypicals} from '../../../../hooks/services/useCabinetTypicals';
import {Helmet} from 'react-helmet';
import {IPersonaProps, Label, Spinner, TextField} from '@fluentui/react';
import {EditCabinetTypicalDto} from '../../../../services/generated/models/EditCabinetTypicalDto';
import styles from './EditCabinetTypical.module.scss';
import {TheButton} from '../../../common/TheButton';

import {PeoplePicker} from '../../../common/PeoplePicker';
import {CustomComboBox} from '../../../common/ComboBox/CustomComboBox';
import {
  accessToSourcesTypeOption,
  cabinetAccessTypeOption,
  cabinetTypeOption,
  cabinetTypicalStatusOption,
  cableEntryTypeOption,
  controlSystemTypeOption,
  forExAreasTypeOption,
  industryTypeOption,
  nativeToolOption,
  numberOfControllersOptions,
  powerInputOption,
  responsibilityTypeOption,
  signalsCountOption,
  systemTypeOption,
  yesNoBothOption,
  yesNoOption
} from './DropdownsValues';

interface IEditCabinetTypical {
  cabinetTypicalId: number;
  setIsEditModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  refreshDocuments: () => void;
}

export const EditCabinetTypical: FC<IEditCabinetTypical> = ({
  cabinetTypicalId,
  setIsEditModalVisible,
  refreshDocuments
}) => {
  const {getCabinetTypicalLatestVersionForEdit, updateCabinetTypical} = useCabinetTypicals();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [typicalId, setTypicalId] = useState<string | null>(null);
  const [typicalImsId, setTypicalImsId] = useState<string | null>(null);
  const [nativeTool, setNativeTool] = useState<string | null>(null);
  const [responsibility, setResponsibility] = useState<string | null>(null);
  const [cabinetType, setCabinetType] = useState<string | null>(null);
  const [height, setHeight] = useState<string | null>(null);
  const [width, setWidth] = useState<string | null>(null);
  const [depth, setDepth] = useState<string | null>(null);
  const [plinth, setPlinth] = useState<string | null>(null);
  const [cableEntry, setCableEntry] = useState<string | null>(null);
  const [cabinetAccess, setCabinetAccess] = useState<string | null>(null);
  const [cabinetSwingFrame, setCabinetSwingFrame] = useState<string | null>(null);
  const [equipmentOnDoor, setEquipmentOnDoor] = useState<string | null>(null);
  const [controlSystem, setControlSystem] = useState<string | null>(null);
  const [system, setSystem] = useState<string | null>(null);
  const [numberOfControllers, setNumberOfControllers] = useState<string | null>(null);
  const [cpuRedundancy, setCpuRedundancy] = useState<string | null>(null);
  const [powerInput, setPowerInput] = useState<string | null>(null);
  const [powerSupplyRedundancy, setPowerSupplyRedundancy] = useState<string | null>(null);
  const [ioRedundancy, setIoRedundancy] = useState<string | null>(null);
  const [isIOSignals, setIsIOSignals] = useState<string | null>(null);
  const [ioSignalsCount, setIoSignalsCount] = useState<string | null>(null);
  const [forExAreasType, setForExAreasType] = useState<string | null>(null);
  const [ip, setIp] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [accessToSourcesTitle, setAccessToSourcesTitle] = useState<string | null>(null);
  const [accessToSourcesUrl, setAccessToSourcesUrl] = useState<string | null>(null);
  const [comment, setComment] = useState<string | null>(null);
  const [industry, setIndustry] = useState<string | null>(null);
  const [manufacturedBy, setManufacturedBy] = useState<string | null>(null);
  const [selectedApprover, setSelectedApprover] = useState<string | null>(null);
  const [selectedPreparedBy, setSelectedPreparedBy] = useState<string | null>(null);
  const [selectedDesigneRespPerson, setSelectedDesigneRespPerson] = useState<string | null>(null);
  const [docStatus, setDocStatus] = useState<string | null>(null);

  const [heightErrorMessage, setHeightErrorMessage] = useState<string | undefined>(undefined);
  const [widthErrorMessage, setWidthErrorMessage] = useState<string | undefined>(undefined);
  const [depthErrorMessage, setDepthErrorMessage] = useState<string | undefined>(undefined);
  const [plinthErrorMessage, setPlinthErrorMessage] = useState<string | undefined>(undefined);
  const [nativeToolErrorMessage, setNativeToolErrorMessage] = useState<string | undefined>(undefined);
  const [responsibilityErrorMessage, setResponsibilityErrorMessage] = useState<string | undefined>(undefined);
  const [cabinetTypeErrorMessage, setCabinetTypeErrorMessage] = useState<string | undefined>(undefined);
  const [cableEntryErrorMessage, setCableEntryErrorMessage] = useState<string | undefined>(undefined);
  const [cabinetAccessErrorMessage, setCabinetAccessErrorMessage] = useState<string | undefined>(undefined);
  const [cabinetSwingFrameErrorMessage, setCabinetSwingFrameErrorMessage] = useState<string | undefined>(undefined);
  const [equipmentOnDoorErrorMessage, setEquipmentOnDoorErrorMessage] = useState<string | undefined>(undefined);
  const [controlSystemErrorMessage, setControlSystemErrorMessage] = useState<string | undefined>(undefined);
  const [systemErrorMessage, setSystemErrorMessage] = useState<string | undefined>(undefined);
  const [numberOfControllersErrorMessage, setNumberOfControllersErrorMessage] = useState<string | undefined>(undefined);
  const [cpuRedundancyErrorMessage, setCpuRedundancyErrorMessage] = useState<string | undefined>(undefined);
  const [powerInputErrorMessage, setPowerInputErrorMessage] = useState<string | undefined>(undefined);
  const [powerSupplyRedundancyErrorMessage, setPowerSupplyRedundancyErrorMessage] = useState<string | undefined>(
    undefined)
  const [ioRedundancyErrorMessage, setIoRedundancyErrorMessage] = useState<string | undefined>(undefined);
  const [isIOSignalsErrorMessage, setIsIOSignalsErrorMessage] = useState<string | undefined>(undefined);
  const [ioSignalsCountErrorMessage, setIoSignalsCountErrorMessage] = useState<string | undefined>(undefined);
  const [forExAreasTypeErrorMessage, setForExAreasTypeErrorMessage] = useState<string | undefined>(undefined);
  const [ipErrorMessage, setIpErrorMessage] = useState<string | undefined>(undefined);
  const [projectNameErrorMessage, setProjectNameErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [accessToSourcesTitleErrorMessage, setAccessToSourcesTitleErrorMessage] = useState<string | undefined>(undefined);

  const validateForm = useCallback(() => {
    let isValid = true;
    if (!height || height.trim() === '') {
      setHeightErrorMessage('Height is required');
      isValid = false;
    }
    if (!width || width.trim() === '') {
      setWidthErrorMessage('Width is required');
      isValid = false;
    }
    if (!depth || depth.trim() === '') {
      setDepthErrorMessage('Depth is required');
      isValid = false;
    }
    if (!plinth || plinth.trim() === '') {
      setPlinthErrorMessage('Plinth is required');
      isValid = false;
    }
    if (!projectName || projectName.trim() === '') {
      setProjectNameErrorMessage('Project Name is required');
      isValid = false;
    }
    if (!nativeTool || nativeTool.trim() === '') {
      setNativeToolErrorMessage('Native Tool is required');
      isValid = false;
    }
    if (!responsibility || responsibility.trim() === '') {
      setResponsibilityErrorMessage('Responsibility is required');
      isValid = false;
    }
    if (!cabinetType || cabinetType.trim() === '') {
      setCabinetTypeErrorMessage('Cabinet Type is required');
      isValid = false;
    }
    if (!cableEntry || cableEntry.trim() === '') {
      setCableEntryErrorMessage('Cable Entry is required');
      isValid = false;
    }
    if (!cabinetAccess || cabinetAccess.trim() === '') {
      setCabinetAccessErrorMessage('Cabinet Access is required');
      isValid = false;
    }
    if (!cabinetSwingFrame || cabinetSwingFrame.trim() === '') {
      setCabinetSwingFrameErrorMessage('Cabinet Swing Frame is required');
      isValid = false;
    }
    if (!equipmentOnDoor || equipmentOnDoor.trim() === '') {
      setEquipmentOnDoorErrorMessage('Equipment on Door is required');
      isValid = false;
    }
    if (!controlSystem || controlSystem.trim() === '') {
      setControlSystemErrorMessage('Control System is required');
      isValid = false;
    }
    if (!system || system.trim() === '') {
      setSystemErrorMessage('System is required');
      isValid = false;
    }
    if (!numberOfControllers || numberOfControllers.trim() === '') {
      setNumberOfControllersErrorMessage('Number of Controllers is required');
      isValid = false;
    }
    if (!cpuRedundancy || cpuRedundancy.trim() === '') {
      setCpuRedundancyErrorMessage('CPU Redundancy is required');
      isValid = false;
    }
    if (!powerInput || powerInput.trim() === '') {
      setPowerInputErrorMessage('Power Input is required');
      isValid = false;
    }
    if (!powerSupplyRedundancy || powerSupplyRedundancy.trim() === '') {
      setPowerSupplyRedundancyErrorMessage('Power Supply Redundancy is required');
      isValid = false;
    }
    if (!ioRedundancy || ioRedundancy.trim() === '') {
      setIoRedundancyErrorMessage('IO Redundancy is required');
      isValid = false;
    }
    if (!isIOSignals || isIOSignals.trim() === '') {
      setIsIOSignalsErrorMessage('Is IO Signals is required');
      isValid = false;
    }
    if (!ioSignalsCount || ioSignalsCount.trim() === '') {
      setIoSignalsCountErrorMessage('IO Signals Count is required');
      isValid = false;
    }
    if (!forExAreasType || forExAreasType.trim() === '') {
      setForExAreasTypeErrorMessage('For Ex Areas Type is required');
      isValid = false;
    }
    if (!ip || ip.trim() === '') {
      setIpErrorMessage('IP>=55 is required');
      isValid = false;
    }

    if (!accessToSourcesTitle || accessToSourcesTitle.trim() === '') {
      setAccessToSourcesTitleErrorMessage('Access To Sources is required');
      isValid = false;
    }

    return isValid;
  }, [height, nativeTool, width, depth, plinth, projectName, responsibility, cabinetType, cableEntry, cabinetAccess, cabinetSwingFrame, equipmentOnDoor, controlSystem, system, numberOfControllers, cpuRedundancy, powerInput, powerSupplyRedundancy, ioRedundancy, isIOSignals, ioSignalsCount, forExAreasType, ip, accessToSourcesTitle]);

  const onSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    var data: EditCabinetTypicalDto = {
      id: cabinetTypicalId,
      typicalId: typicalId,
      typicalImsId: typicalImsId,
      nativeTool: nativeTool,
      responsibility: responsibility,
      cabinetType: cabinetType,
      height: height,
      width: width,
      depth: depth,
      plinth: plinth,
      cableEntry: cableEntry,
      cabinetAccess: cabinetAccess,
      cabinetSwingFrame: cabinetSwingFrame,
      equipmentOnDoor: equipmentOnDoor,
      controlSystem: controlSystem,
      system: system,
      numberOfControllers: numberOfControllers,
      cpuRedundancy: cpuRedundancy,
      powerInput: powerInput,
      powerSupplyRedundancy: powerSupplyRedundancy,
      ioRedundancy: ioRedundancy,
      isIOSignals: isIOSignals,
      ioSignalsCount: ioSignalsCount,
      forExAreasType: forExAreasType,
      ip: ip,
      projectName: projectName,
      accessToSourcesTitle: accessToSourcesTitle,
      accessToSourcesUrl: accessToSourcesUrl,
      comment: comment,
      industry: industry,
      manufacturedBy: manufacturedBy,
      approver: selectedApprover,
      preparedBy: selectedPreparedBy,
      designeRespPerson: selectedDesigneRespPerson,
      docStatus: docStatus
    };

    await updateCabinetTypical(cabinetTypicalId, data);

    setIsLoading(false);
    setIsEditModalVisible(false);
    refreshDocuments();
  }, [
    updateCabinetTypical,
    refreshDocuments,
    validateForm,
    setIsEditModalVisible,
    cabinetTypicalId,
    typicalImsId,
    selectedApprover,
    selectedDesigneRespPerson,
    selectedPreparedBy,
    nativeTool,
    plinth,
    height,
    width,
    depth,
    ip,
    cabinetAccess,
    numberOfControllers,
    equipmentOnDoor,
    cableEntry,
    manufacturedBy,
    controlSystem,
    comment,
    responsibility,
    cabinetType,
    cabinetSwingFrame,
    system,
    cpuRedundancy,
    powerInput,
    powerSupplyRedundancy,
    ioRedundancy,
    isIOSignals,
    ioSignalsCount,
    forExAreasType,
    projectName,
    accessToSourcesTitle,
    accessToSourcesUrl,
    industry,
    docStatus,
    typicalId
  ]);

  const onCloseModal = () => {
    setIsEditModalVisible(false);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const cabinetTypicalForEdit = await getCabinetTypicalLatestVersionForEdit(cabinetTypicalId);
      if (cabinetTypicalForEdit && cabinetTypicalForEdit.result) {
        setTypicalId(cabinetTypicalForEdit.result.typicalId);
        setTypicalImsId(cabinetTypicalForEdit.result.typicalImsId);
        setNativeTool(cabinetTypicalForEdit.result.nativeTool);
        setResponsibility(cabinetTypicalForEdit.result.responsibility);
        setCabinetType(cabinetTypicalForEdit.result.cabinetType);
        setHeight(cabinetTypicalForEdit.result.height);
        setWidth(cabinetTypicalForEdit.result.width);
        setDepth(cabinetTypicalForEdit.result.depth);
        setPlinth(cabinetTypicalForEdit.result.plinth);
        setCableEntry(cabinetTypicalForEdit.result.cableEntry);
        setCabinetAccess(cabinetTypicalForEdit.result.cabinetAccess);
        setCabinetSwingFrame(cabinetTypicalForEdit.result.cabinetSwingFrame);
        setEquipmentOnDoor(cabinetTypicalForEdit.result.equipmentOnDoor);
        setControlSystem(cabinetTypicalForEdit.result.controlSystem);
        setSystem(cabinetTypicalForEdit.result.system);
        setNumberOfControllers(cabinetTypicalForEdit.result.numberOfControllers);
        setCpuRedundancy(cabinetTypicalForEdit.result.cpuRedundancy);
        setPowerInput(cabinetTypicalForEdit.result.powerInput);
        setPowerSupplyRedundancy(cabinetTypicalForEdit.result.powerSupplyRedundancy);
        setIoRedundancy(cabinetTypicalForEdit.result.ioRedundancy);
        setIsIOSignals(cabinetTypicalForEdit.result.isIOSignals);
        setIoSignalsCount(cabinetTypicalForEdit.result.ioSignalsCount);
        setForExAreasType(cabinetTypicalForEdit.result.forExAreasType);
        setIp(cabinetTypicalForEdit.result.ip);
        setProjectName(cabinetTypicalForEdit.result.projectName);
        setAccessToSourcesTitle(cabinetTypicalForEdit.result.accessToSourcesTitle);
        setAccessToSourcesUrl(cabinetTypicalForEdit.result.accessToSourcesUrl);
        setComment(cabinetTypicalForEdit.result.comment);
        setIndustry(cabinetTypicalForEdit.result.industry);
        setManufacturedBy(cabinetTypicalForEdit.result.manufacturedBy);
        setSelectedApprover(cabinetTypicalForEdit.result.approver);
        setSelectedDesigneRespPerson(cabinetTypicalForEdit.result.designeRespPerson);
        setSelectedPreparedBy(cabinetTypicalForEdit.result.preparedBy);
        setDocStatus(cabinetTypicalForEdit.result.docStatus);
      }
      setIsLoading(false);
    })();
  }, [getCabinetTypicalLatestVersionForEdit, cabinetTypicalId]);

  const handleHeight = (value: string | null) => {
    setHeightErrorMessage(undefined);
    if (value == null || value.trim() === '') {
      setHeightErrorMessage('Height is required');
    }
    setHeight(value);
  };

  const handleWidth = (value: string | null) => {
    setWidthErrorMessage(undefined);
    if (value == null || value.trim() === '') {
      setWidthErrorMessage('Width is required');
    }
    setWidth(value);
  };

  const handleDepth = (value: string | null) => {
    setDepthErrorMessage(undefined);
    if (value == null || value.trim() === '') {
      setDepthErrorMessage('Depth is required');
    }
    setDepth(value);
  };

  const handlePlinth = (value: string | null) => {
    setPlinthErrorMessage(undefined);
    if (value == null || value.trim() === '') {
      setPlinthErrorMessage('Plinth is required');
    }
    setPlinth(value);
  };

  const handleProjectName = (value: string | null) => {
    setProjectNameErrorMessage(undefined);
    if (value == null || value.trim() === '') {
      setProjectNameErrorMessage('Project Name is required');
    }
    setProjectName(value);
  };

  const handleApprover = (value: IPersonaProps[] | undefined) => {
    if (value && value.length > 0) {
      setSelectedApprover((value[0].key as string) || null);
    } else {
      setSelectedApprover(null);
    }
  };

  const handleDesigneRespPerson = (value: IPersonaProps[] | undefined) => {
    if (value && value.length > 0) {
      setSelectedDesigneRespPerson((value[0].key as string) || null);
    } else {
      setSelectedDesigneRespPerson(null);
    }
  };

  const handlePreparedBy = (value: IPersonaProps[] | undefined) => {
    if (value && value.length > 0) {
      setSelectedPreparedBy((value[0].key as string) || null);
    } else {
      setSelectedPreparedBy(null);
    }
  };

  return (
    <>
      <div className={`${isLoading ? 'isLoading' : ''} noPaddingTop`}>
        {
          <Helmet>
            <title>{`IMS Edit Cabinet Typical: ${typicalId}`} </title>
          </Helmet>
        }
        {isLoading ? (
          <Spinner />
        ) : (
          <div className={styles.editCabinetTypicalContainer}>
            <div className={styles.readOnlyContainer}>
              <TextField
                className={styles.title}
                label="Typical Id"
                value={typicalId || undefined}
                readOnly
                disabled
              />
              <TextField
                className={styles.title}
                label="Typical IMS Id"
                value={typicalImsId || undefined}
                readOnly
                disabled
              />
            </div>
            <div className={styles.fieldsContainer}>
              <CustomComboBox
                label="Native Tool"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setNativeToolErrorMessage('Native Tool is required');
                    setNativeTool(null);
                  }
                }}
                onChanged={option => {
                    if(option){
                      setNativeTool(option.key as string);
                      setNativeToolErrorMessage(undefined);
                    }
                    
                  
                }}
                errorMessage={nativeToolErrorMessage}
                defaultOptions={nativeToolOption}
                defaultSelectedKey={nativeTool}
              />
              <CustomComboBox
                label="Responsibility"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setResponsibilityErrorMessage('Responsibility is required');
                    setResponsibility(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setResponsibility(option.key as string);
                    setResponsibilityErrorMessage(undefined);
                  }
                }}
                errorMessage={responsibilityErrorMessage}
                defaultOptions={responsibilityTypeOption}
                defaultSelectedKey={responsibility}
              />
              <CustomComboBox
                label="Cabinet Type"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setCabinetTypeErrorMessage('Cabinet Type is required');
                    setCabinetType(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setCabinetType(option.key as string);
                    setCabinetTypeErrorMessage(undefined);
                  }
                }}
                errorMessage={cabinetTypeErrorMessage}
                defaultOptions={cabinetTypeOption}
                defaultSelectedKey={cabinetType}
              />

              <TextField
                className={styles.textField}
                label="Height"
                value={height || undefined}
                onChange={(e, newValue) => handleHeight(newValue === undefined ? null : newValue)}
                errorMessage={heightErrorMessage}
              />
            </div>
            <div className={styles.fieldsContainer}>
              <TextField
                className={styles.textField}
                label="Width"
                value={width || undefined}
                onChange={(e, newValue) => handleWidth(newValue === undefined ? null : newValue)}
                errorMessage={widthErrorMessage}
              />
              <TextField
                className={styles.textField}
                label="Depth"
                value={depth || undefined}
                onChange={(e, newValue) => handleDepth(newValue === undefined ? null : newValue)}
                errorMessage={depthErrorMessage}
              />
              <TextField
                className={styles.textField}
                label="Plinth"
                value={plinth || undefined}
                onChange={(e, newValue) => handlePlinth(newValue === undefined ? null : newValue)}
                errorMessage={plinthErrorMessage}
              />

              <CustomComboBox
                label="Cable Entry"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setCableEntryErrorMessage('Cable Entry is required');
                    setCableEntry(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setCableEntry(option.key as string);
                    setCableEntryErrorMessage(undefined);
                  }
                }}
                errorMessage={cableEntryErrorMessage}
                defaultOptions={cableEntryTypeOption}
                defaultSelectedKey={cableEntry}
              />
            </div>
            <div className={styles.fieldsContainer}>
              <CustomComboBox
                label="Cabinet Access"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setCabinetAccessErrorMessage('Cabinet Access is required');
                    setCabinetAccess(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setCabinetAccess(option.key as string);
                    setCabinetAccessErrorMessage(undefined);
                  }
                }}
                errorMessage={cabinetAccessErrorMessage}
                defaultOptions={cabinetAccessTypeOption}
                defaultSelectedKey={cabinetAccess}
              />
              <CustomComboBox
                label="Cabinet Swing Frame"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setCabinetSwingFrameErrorMessage('Cabinet Swing Frame is required');
                    setCabinetSwingFrame(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setCabinetSwingFrame(option.key as string);
                    setCabinetSwingFrameErrorMessage(undefined);
                  }
                }}
                errorMessage={cabinetSwingFrameErrorMessage}
                defaultOptions={yesNoOption}
                defaultSelectedKey={cabinetSwingFrame}
              />
              <CustomComboBox
                label="Equipment On Door"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setEquipmentOnDoorErrorMessage('Equipment on Door is required');
                    setEquipmentOnDoor(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setEquipmentOnDoor(option.key as string);
                    setEquipmentOnDoorErrorMessage(undefined);
                  }
                }}
                errorMessage={equipmentOnDoorErrorMessage}
                defaultOptions={yesNoOption}
                defaultSelectedKey={equipmentOnDoor}
              />

              <CustomComboBox
                label="Control System"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setControlSystemErrorMessage('Control System is required');
                    setControlSystem(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setControlSystem(option.key as string);
                    setControlSystemErrorMessage(undefined);
                  }
                }}
                errorMessage={controlSystemErrorMessage}
                defaultOptions={controlSystemTypeOption}
                defaultSelectedKey={controlSystem}
              />
            </div>
            <div className={styles.fieldsContainer}>
              <CustomComboBox
                label="System"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setSystemErrorMessage('System is required');
                    setSystem(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setSystem(option.key as string);
                    setSystemErrorMessage(undefined);
                  }
                }}
                errorMessage={systemErrorMessage}
                defaultOptions={systemTypeOption}
                defaultSelectedKey={system}
              />
              <CustomComboBox
                label="Number Of Controllers"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setNumberOfControllersErrorMessage('Number Of Controllers is required');
                    setNumberOfControllers(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setNumberOfControllers(option.key as string);
                    setNumberOfControllersErrorMessage(undefined);
                  }
                }}
                errorMessage={numberOfControllersErrorMessage}
                defaultOptions={numberOfControllersOptions}
                defaultSelectedKey={numberOfControllers}
              />
              <CustomComboBox
                label="CPU Redundancy"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setCpuRedundancyErrorMessage('CPU Redundancy is required');
                    setCpuRedundancy(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setCpuRedundancy(option.key as string);
                    setCpuRedundancyErrorMessage(undefined);
                  }
                }}
                errorMessage={cpuRedundancyErrorMessage}
                defaultOptions={yesNoOption}
                defaultSelectedKey={cpuRedundancy}
              />

              <CustomComboBox
                label="Power Input"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setPowerInputErrorMessage('Power Input is required');
                    setPowerInput(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setPowerInput(option.key as string);
                    setPowerInputErrorMessage(undefined);
                  }
                }}
                errorMessage={powerInputErrorMessage}
                defaultOptions={powerInputOption}
                defaultSelectedKey={powerInput}
              />
            </div>
            <div className={styles.fieldsContainer}>
              <CustomComboBox
                label="Power Supply Redundancy"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setPowerSupplyRedundancyErrorMessage('Power Supply Redundancy is required');
                    setPowerSupplyRedundancy(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setPowerSupplyRedundancy(option.key as string);
                    setPowerSupplyRedundancyErrorMessage(undefined);
                  }
                }}
                errorMessage={powerSupplyRedundancyErrorMessage}
                defaultOptions={yesNoOption}
                defaultSelectedKey={powerSupplyRedundancy}
              />
              <CustomComboBox
                label="IO Redundancy"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setIoRedundancyErrorMessage('IO Redundancy is required');
                    setIoRedundancy(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setIoRedundancy(option.key as string);
                    setIoRedundancyErrorMessage(undefined);
                  }
                }}
                errorMessage={ioRedundancyErrorMessage}
                defaultOptions={yesNoOption}
                defaultSelectedKey={ioRedundancy}
              />
              <CustomComboBox
                label="Is IO Signals"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setIsIOSignalsErrorMessage('Is IO Signals is required');
                    setIsIOSignals(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setIsIOSignals(option.key as string);
                    setIsIOSignalsErrorMessage(undefined);
                  }
                }}
                errorMessage={isIOSignalsErrorMessage}
                defaultOptions={yesNoBothOption}
                defaultSelectedKey={isIOSignals}
              />

              <CustomComboBox
                label="IO Signals Count"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setIoSignalsCountErrorMessage('IO Signals Count is required');
                    setIoSignalsCount(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setIoSignalsCount(option.key as string);
                    setIoSignalsCountErrorMessage(undefined);
                  }
                }}
                errorMessage={ioSignalsCountErrorMessage}
                defaultOptions={signalsCountOption}
                defaultSelectedKey={ioSignalsCount}
              />
            </div>
            <div className={styles.fieldsContainer}>
              <CustomComboBox
                label="For Ex Areas"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setForExAreasTypeErrorMessage('For Ex Areas Type is required');
                    setForExAreasType(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setForExAreasType(option.key as string);
                    setForExAreasTypeErrorMessage(undefined);
                  }
                }}
                errorMessage={forExAreasTypeErrorMessage}
                defaultOptions={forExAreasTypeOption}
                defaultSelectedKey={forExAreasType}
              />
              <CustomComboBox
                label="IP>=55"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setIpErrorMessage('IP>=55 is required');
                    setIp(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setIp(option.key as string);
                    setIpErrorMessage(undefined);
                  }
                }}
                errorMessage={ipErrorMessage}
                defaultOptions={yesNoOption}
                defaultSelectedKey={ip}
              />
              <TextField
                className={styles.textField}
                label="Project Name"
                value={projectName || undefined}
                onChange={(e, newValue) =>
                  handleProjectName(newValue === undefined ? null : newValue)
                }
                errorMessage={projectNameErrorMessage}
              />

              <CustomComboBox
                label="Access To Sources"
                blockFreeForm={true}
                setTitleValue={setAccessToSourcesTitle}
                setUrlValue={setAccessToSourcesUrl}
                onChanged={option => {
                  if (option) {
                    setAccessToSourcesTitle(option.text as string);
                    setAccessToSourcesUrl(option.key as string);
                    setAccessToSourcesTitleErrorMessage(undefined);
                  }
                }}
                errorMessage={accessToSourcesTitleErrorMessage}
                addUrlOption={true}
                defaultOptions={accessToSourcesTypeOption}
                defaultSelectedKey={accessToSourcesUrl}
                defaultSelectedText={accessToSourcesTitle || undefined}
              />
            </div>
            <div className={styles.fieldsContainer}>
              <CustomComboBox
                label="Industry"
                onInputValueChange={value => {
                  if(!value || value.trim() === ''){
                    setIndustry(null);
                  }
                }}
                onChanged={option => {
                  if (option) {
                    setIndustry(option.key as string);
                  }
                }}
                defaultOptions={industryTypeOption}
                defaultSelectedKey={industry}
              />
              <TextField
                className={styles.textField}
                label="Manufactured By"
                value={manufacturedBy || undefined}
                onChange={(e, newValue) =>
                  setManufacturedBy(newValue === undefined ? null : newValue)
                }
              />
              <CustomComboBox
                label="Doc Status"
                onChanged={option => {
                  if (option) {
                    setDocStatus(option.key as string);
                  }
                }}
                defaultOptions={cabinetTypicalStatusOption}
                defaultSelectedKey={docStatus}
              />
              <div className={styles.textField}>
                <Label htmlFor={'peoplePicker'}>Approver</Label>
                <PeoplePicker
                  key={'peoplePicker'}
                  defaultSelectedPersonId={selectedApprover || undefined}
                  onSelectPerson={value => handleApprover(value)}
                  itemLimit={1}
                />
            </div>
            </div>
            <div className={styles.peoplePickerContainer}>
              <div className={styles.peoplePicker}>
                <Label htmlFor={'peoplePicker'}>Prepared By</Label>
                <PeoplePicker
                  key={'peoplePicker'}
                  defaultSelectedPersonId={selectedPreparedBy || undefined}
                  onSelectPerson={value => handlePreparedBy(value)}
                  itemLimit={1}
                />
              </div>
              <div className={styles.peoplePicker}>
                <Label htmlFor={'peoplePicker'}>Design Responsible Person</Label>
                <PeoplePicker
                  key={'peoplePicker'}
                  defaultSelectedPersonId={selectedDesigneRespPerson || undefined}
                  onSelectPerson={value => handleDesigneRespPerson(value)}
                  itemLimit={1}
                />
              </div>
            </div>
            <TextField
              className={styles.description}
              multiline
              label="Comment"
              value={comment || undefined}
              onChange={(e, newValue) => setComment(newValue === undefined ? null : newValue)}
            />
            <footer className={styles.buttons}>
              <TheButton disabled={isLoading} onClick={onCloseModal}>
                Close
              </TheButton>
              <TheButton primary disabled={isLoading} onClick={onSubmit}>
                Update
              </TheButton>
            </footer>
          </div>
        )}
      </div>
      
    </>
  );
};
